import { useEffect } from 'react';

/**
 * Adds scroll-wheel panning to a chart wrapper.
 * Only activates when the chart is zoomed in (zoomDomainRef.current !== null).
 * Calls e.preventDefault() to stop page scroll while zoomed.
 *
 * Handles trackpad horizontal swipe (deltaX) and mouse scroll wheel (deltaY).
 *
 * @param chartWrapRef  - ref to the chart wrapper DOM element
 * @param zoomDomainRef - ref to current [lo, hi] zoom domain, or null
 * @param leftPad       - Y-axis width in px (left side of chart area)
 * @param rightPadRef   - ref whose .current is the right margin px
 * @param onPanFtRef    - ref to fn(deltaFt: number) — apply this feet delta to the zoom domain
 */
export function usePanOnWheel({ chartWrapRef, zoomDomainRef, leftPad, rightPadRef, onPanFtRef }) {
  useEffect(() => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;

    const handler = (e) => {
      const zoom = zoomDomainRef.current;
      if (!zoom) return;

      e.preventDefault();

      // Prefer horizontal delta (trackpad swipe), fall back to vertical (scroll wheel)
      const raw = Math.abs(e.deltaX) >= Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      let pixelDelta = raw;
      if (e.deltaMode === 1) pixelDelta *= 18;       // line mode
      else if (e.deltaMode === 2) pixelDelta *= 300;  // page mode
      if (!pixelDelta) return;

      const chartW = wrap.clientWidth - leftPad - rightPadRef.current;
      if (chartW <= 0) return;

      const ftPerPx = (zoom[1] - zoom[0]) / chartW;
      onPanFtRef.current(pixelDelta * ftPerPx);
    };

    wrap.addEventListener('wheel', handler, { passive: false });
    return () => wrap.removeEventListener('wheel', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
